/**
 * @file src/pages/authentication-modal/useLogout.js
 * @description React Query mutation hook for user logout.
 *
 * What changed from the original:
 *  - Import from utils/userApis → api/auth.api.js
 *  - Redux dispatch(logoutUser) removed — loginSlice deleted
 *  - 6 individual queryClient.removeQueries → queryClient.clear()
 *    Clears the entire cache in one call. No risk of missing a key.
 *  - Redirects to marketing site on logout
 *  - Dispatches "user/logoutUser" action so sseMiddleware closes
 *    the SSE connection cleanly on logout
 *  - console.error removed
 *  - onError still redirects — correct, logout should always
 *    clear local state even if the server call fails
 *
 * Rules:
 *  - No Redux state reads — only dispatch for SSE teardown signal
 *  - No direct axios
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { logoutUser as logoutAction } from "api/auth.api";
import { resetInvoiceProgress } from "pages/invoice-processing-queue/invoiceProgressSlice";

const MARKETING_SITE_URL = "https://www.auditguardai.com";

/**
 * Mutation hook for logging out the current user.
 *
 * On success AND error:
 *  1. Clears entire React Query cache
 *  2. Dispatches Redux action to close SSE connection
 *  3. Resets invoice progress slice
 *  4. Redirects to marketing site
 *
 * Usage:
 *  const { mutate: logout, isLoading } = useLogout();
 *  <button onClick={() => logout()}>Logout</button>
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  /**
   * Centralized cleanup — runs on both success and error.
   * Logout must always clear local state regardless of server response.
   */
  const cleanup = () => {
    // 1. Clear entire React Query cache — one call, no missed keys
    queryClient.clear();

    // 2. Signal sseMiddleware to close the SSE connection
    //    sseMiddleware listens for "user/logoutUser" action type
    dispatch({ type: "user/logoutUser" });

    // 3. Reset invoice progress Redux state
    dispatch(resetInvoiceProgress());

    // 4. Redirect to marketing site
    window.location.replace(MARKETING_SITE_URL);
  };

  return useMutation({
    mutationFn: logoutAction,
    onSuccess: cleanup,
    onError: cleanup, // Always clean up — even if server logout fails
  });
};
